/**
 * [SUB-PLAN 1H-K — 2026-05-29]: Per-instance item custom-choices resolver.
 *
 * Resolves a BASE_ITEMS customChoices config + a player's per-instance picks
 * into a concrete effects array. Mirrors the feat customChoices pattern in
 * useFeatEffects.js (L81-101) but for items — keeping items and feats on
 * the same substrate.
 *
 * CONFIG SHAPE (on BASE_ITEMS row):
 *   customChoices: {
 *     <fieldKey>: {
 *       kind: 'damageResistanceType',   // composerCatalogRegistry kind key (for picker UI)
 *       label: 'Resistance Type',       // UI label
 *       emits: {                        // effect template for the picked value
 *         type: 'resistance',
 *         operation: 'grant'
 *         // value is injected at resolve-time from the pick
 *       }
 *     }
 *   }
 *
 * INSTANCE SHAPE (on item in inventory):
 *   customChoices: { <fieldKey>: '<pickedValue>' }
 *
 * OUTPUT: effects array directly writable to item.effects[].
 *   These are consumed by buildItemRegistrationsFromInventory (unchanged bridge).
 *
 * RULES:
 *   - Missing config → returns [].
 *   - Missing or empty instance choices → returns [].
 *   - Missing pick for a given field → that field emits no effect (skipped).
 *   - Multiple fields supported.
 *   - Pure function — no side effects, no imports of React or services.
 *
 * [RULE 22]: Pure function, importable by both production code AND tests.
 *   Tests call this directly then feed the result into buildItemRegistrationsFromInventory.
 */

/**
 * resolveCustomChoiceEffects
 *
 * @param {Object|null} itemDef - BASE_ITEMS row (has .customChoices config)
 * @param {Object|null} instanceChoices - per-instance picked values ({ fieldKey: value })
 * @returns {Array} resolved effects array (may be empty; never null)
 */
export function resolveCustomChoiceEffects(itemDef, instanceChoices) {
  // Guard: no config → nothing to resolve
  if (!itemDef || !itemDef.customChoices || typeof itemDef.customChoices !== 'object') {
    return [];
  }

  // Guard: no instance picks → nothing to resolve
  if (!instanceChoices || typeof instanceChoices !== 'object') {
    return [];
  }

  const resolved = [];

  Object.entries(itemDef.customChoices).forEach(([fieldKey, choiceConfig]) => {
    // Skip malformed entries
    if (!choiceConfig || typeof choiceConfig !== 'object') return;

    const pickedValue = instanceChoices[fieldKey];
    // Skip unpicked fields
    if (!pickedValue || typeof pickedValue !== 'string' || pickedValue.trim() === '') return;

    const emits = choiceConfig.emits;
    if (!emits || typeof emits !== 'object') return;

    // Build the effect by spreading the emits template and injecting value.
    // Convention: the picked value becomes the effect's `value` property.
    resolved.push({
      ...emits,
      value: pickedValue,
    });
  });

  return resolved;
}
